import * as React from "react"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { useReducedMotion } from "motion/react"

import { useAppState } from "../../context/appStateContext"
import native from "../../native/native"
import { formatCurrency } from "../../utils/utils"
import svg from "../../utils/svg"
import styles from "./StickerAlbumPaywall.module.scss"

/**
 * StickerAlbumPaywall (`/album`) — an original, self-contained ONBOARDING
 * paywall for Private Photo Vault Premium. Paywall type rolled at random for
 * this run: ONBOARDING (first-run / day-one, not expiration, not a mid-app
 * upsell). No Slack style directive is active this run (the newest
 * @Paywalls Bot mention was `default` — the explicit reset keyword), so this
 * is a free-choice, full-divergence concept.
 *
 * ── Concept ────────────────────────────────────────────────────────────────
 * Day one is framed as starting a glossy COLLECTOR'S STICKER ALBUM of your own
 * memories. Your first photos sit on a peel-sheet in the tray; each one PEELS
 * up (a curl + specular glint) and STICKS into a labelled foil slot with a
 * satisfying settle-pop. Filling the album is the whole screen. The pitch is
 * pure aspirational collector-PRIDE — "make it yours, and keep it filling
 * itself" — a deliberate pivot away from the recent run of dark, loss-aversion
 * / fear paywalls.
 *
 * ── Why it converts (honest, behavioural) ─────────────────────────────────
 * - IKEA / endowed-progress: you place the stickers yourself, so the album
 *   feels like *yours* before you ever pay.
 * - Goal-gradient + Zeigarnik: a live "N / N stuck in" counter and a visibly
 *   incomplete page pull you to finish the set.
 * - Contrast built into pricing: YEARLY is "the album that fills itself all
 *   year — every new photo auto-added"; monthly is the small "just this page".
 *   The featured act (filling the album) maps onto the featured YEARLY plan.
 *
 * ── Honesty / App-Store compliance ────────────────────────────────────────
 * - Additive framing only: empty slots are memories you *haven't added yet*,
 *   never memories that vanish without Premium. Stickers are full-colour from
 *   the first frame (deliberately avoids the overused grey→colour reveal).
 * - Nothing ever spills / falls / is lost; no countdown, no fake scarcity, no
 *   invented features. Every capability shown is a real Premium feature.
 * - Renders fully on built-in debug product data; no native injection needed.
 */

type PlanKey = "yearly" | "monthly"

// Warm, candid life photos already bundled in the repo (no network egress).
type Slot = { photo: string; label: string; tilt: number }
const SLOTS: Slot[] = [
  { photo: "m01", label: "Trips", tilt: -3.5 },
  { photo: "m12", label: "Friends", tilt: 2.5 },
  { photo: "m17", label: "Golden hour", tilt: -1.5 },
  { photo: "m14", label: "Weekend", tilt: 3 },
  { photo: "m13", label: "Little ones", tilt: -2.5 },
  { photo: "m05", label: "Family", tilt: 1.5 },
  { photo: "m15", label: "Cozy", tilt: 3.5 },
  { photo: "m16", label: "Everyday", tilt: -3 },
  { photo: "m09", label: "Faves", tilt: 2 },
]
const TOTAL = SLOTS.length

const photoUrl = (name: string) =>
  `${process.env.PUBLIC_URL || ""}/byheart/${name}.jpg`

type Feature = {
  Icon: typeof svg.featureIcons.CloudVault
  title: string
  sub: string
}
const FEATURES: Feature[] = [
  {
    Icon: svg.featureIcons.CloudVault,
    title: "It fills itself",
    sub: "Every new photo & video auto-added",
  },
  {
    Icon: svg.featureIcons.Star,
    title: "Full-quality",
    sub: "Originals kept, never shrunk",
  },
  {
    Icon: svg.featureIcons.Wifi,
    title: "On every device",
    sub: "One private library, everywhere",
  },
  {
    Icon: svg.featureIcons.Breakin,
    title: "Break-In Report",
    sub: "Photo + place of a wrong passcode",
  },
  {
    Icon: svg.featureIcons.Trash,
    title: "Trash Recovery",
    sub: "A 30-day net for deletes",
  },
  {
    Icon: svg.featureIcons.Decoy,
    title: "Decoy & Disguise",
    sub: "A second passcode, a hidden app",
  },
]

function StickerAlbumPaywall() {
  const { state } = useAppState()
  const reduceMotion = useReducedMotion()

  const premium = state.productData?.premium
  const yearlyProduct = premium?.yearlyProduct
  const monthlyProduct = premium?.monthlyProduct
  const locale = yearlyProduct?.priceLocale

  const yearlyPrice = yearlyProduct
    ? formatCurrency(yearlyProduct.price, locale)
    : "$35.99"
  const yearlyPerMonth = yearlyProduct
    ? formatCurrency(yearlyProduct.price / 12, locale)
    : "$2.99"
  const monthlyPrice = monthlyProduct
    ? formatCurrency(monthlyProduct.price, locale)
    : "$13.99"
  const hasTrial = premium?.hasTrial ?? yearlyProduct?.hasTrial ?? true

  const savingsPct = useMemo(() => {
    if (!yearlyProduct || !monthlyProduct) return 79
    return Math.max(
      1,
      Math.round((1 - yearlyProduct.price / (monthlyProduct.price * 12)) * 100),
    )
  }, [yearlyProduct, monthlyProduct])

  const [plan, setPlan] = useState<PlanKey>("yearly")

  // How many stickers are stuck into the album (0..TOTAL).
  const [stuck, setStuck] = useState(0)
  const complete = stuck >= TOTAL
  const timers = useRef<number[]>([])

  // Auto-teach: peel-and-stick the first memories one by one on load, then
  // hand the peel-sheet over. Reduced motion → present the finished album.
  useEffect(() => {
    if (reduceMotion) {
      setStuck(TOTAL)
      return
    }
    const t = timers.current
    for (let i = 1; i <= TOTAL; i++) {
      t.push(
        window.setTimeout(
          () => setStuck((s) => (s < i ? i : s)),
          480 + i * 300,
        ),
      )
    }
    return () => {
      t.forEach(clearTimeout)
      timers.current = []
    }
  }, [reduceMotion])

  // Tapping the peel-sheet (or an empty slot) sticks the next memory in now.
  const stickNext = useCallback(() => {
    timers.current.forEach(clearTimeout)
    timers.current = []
    setStuck((s) => Math.min(TOTAL, s + 1))
  }, [])

  const handleSubscribe = useCallback(() => {
    const product = plan === "yearly" ? yearlyProduct : monthlyProduct
    if (product) native.subscribe(product)
    else native.postNativeEvent("subscribe")
  }, [plan, yearlyProduct, monthlyProduct])

  const remaining = TOTAL - stuck
  const nextSlot = SLOTS[Math.min(stuck, TOTAL - 1)]

  return (
    <div className={`${styles.root} ${complete ? styles.rootDone : ""}`}>
      {/* ── top bar ─────────────────────────────────────────────────────── */}
      <header className={styles.topbar}>
        <div className={styles.brand}>
          <span className={styles.brandDot} aria-hidden="true" />
          <span className={styles.brandName}>Private Photo Vault</span>
          <span className={styles.brandTag}>Premium</span>
        </div>
        <button
          className={styles.close}
          aria-label="Close"
          onClick={() => native.postNativeEvent("close")}
        >
          <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden="true">
            <path
              d="M6 6l12 12M18 6L6 18"
              stroke="currentColor"
              strokeWidth="2.2"
              strokeLinecap="round"
            />
          </svg>
        </button>
      </header>

      {/* ── title + collector progress ──────────────────────────────────── */}
      <div className={styles.head}>
        <h1 className={styles.title}>
          Start your <span className={styles.titleFoil}>private album</span>
        </h1>
        <p className={styles.sub}>
          Peel your first memories in — then Premium keeps stickering every new
          one, all year.
        </p>
        <div
          className={`${styles.progress} ${complete ? styles.progressDone : ""}`}
          aria-live="polite"
        >
          <span className={styles.progressCount}>
            {stuck} / {TOTAL}
          </span>
          <span className={styles.progressWord}>
            {complete ? "album started · only you can open it" : "stuck in"}
          </span>
        </div>
      </div>

      {/* ── the album spread ────────────────────────────────────────────── */}
      <section
        className={`${styles.album} ${complete ? styles.albumDone : ""}`}
        aria-label="Your sticker album"
      >
        <div className={styles.binding} aria-hidden="true">
          {Array.from({ length: 7 }).map((_, i) => (
            <span key={i} className={styles.ring} />
          ))}
        </div>

        {complete && (
          <div className={styles.seal} aria-hidden="true">
            <svg viewBox="0 0 24 24" width="15" height="15">
              <path
                d="M6 11V8a6 6 0 1112 0v3"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
              <rect
                x="4.5"
                y="11"
                width="15"
                height="9.5"
                rx="2.4"
                fill="currentColor"
              />
            </svg>
            <span>Kept private</span>
          </div>
        )}

        <div className={styles.grid}>
          {SLOTS.map((slot, i) => {
            const isStuck = i < stuck
            return (
              <button
                key={slot.photo}
                type="button"
                className={`${styles.slot} ${isStuck ? styles.slotStuck : ""}`}
                style={{ "--tilt": `${slot.tilt}deg` } as React.CSSProperties}
                onClick={() => !isStuck && stickNext()}
                aria-label={
                  isStuck ? `${slot.label}, stuck in` : `Stick ${slot.label} in`
                }
              >
                <span className={styles.slotLabel}>{slot.label}</span>
                <span className={styles.foil} aria-hidden="true" />
                {isStuck && (
                  <span className={styles.sticker}>
                    <img
                      src={photoUrl(slot.photo)}
                      alt=""
                      draggable={false}
                      loading="eager"
                    />
                    <span className={styles.gloss} aria-hidden="true" />
                    <span className={styles.tick} aria-hidden="true">
                      <svg viewBox="0 0 24 24" width="12" height="12">
                        <path
                          d="M5 13l4 4L19 7"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.6"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </span>
                  </span>
                )}
              </button>
            )
          })}
        </div>
      </section>

      {/* ── the peel-sheet (tray of memories waiting to be stuck) ────────── */}
      <div className={styles.trayWrap}>
        {!complete ? (
          <button
            type="button"
            className={styles.tray}
            onClick={stickNext}
            aria-label={`Peel and stick the next memory (${remaining} left)`}
          >
            <span className={styles.peelStack} aria-hidden="true">
              {SLOTS.slice(stuck, stuck + 3)
                .reverse()
                .map((s, idx, arr) => {
                  const depth = arr.length - 1 - idx
                  return (
                    <span
                      key={s.photo}
                      className={`${styles.peel} ${depth === 0 ? styles.peelTop : ""}`}
                      style={
                        {
                          "--d": depth,
                          backgroundImage: `url(${photoUrl(s.photo)})`,
                        } as React.CSSProperties
                      }
                    />
                  )
                })}
            </span>
            <span className={styles.trayText}>
              <span className={styles.trayTitle}>Peel &amp; stick</span>
              <span className={styles.traySub}>
                {remaining} {remaining === 1 ? "memory" : "memories"} left ·{" "}
                {nextSlot.label}
              </span>
            </span>
            <span className={styles.trayArrow} aria-hidden="true">
              ↑
            </span>
          </button>
        ) : (
          <div className={styles.trayDone}>
            <span className={styles.trayDoneStar} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="16" height="16">
                <path
                  d="M12 3l2.6 5.6L21 9.3l-4.5 4.2 1.1 6.2L12 16.8 6.4 19.7l1.1-6.2L3 9.3l6.4-.7z"
                  fill="currentColor"
                />
              </svg>
            </span>
            Your album is started. Keep it filling itself.
          </div>
        )}
      </div>

      {/* ── what Premium keeps doing (real features) ────────────────────── */}
      <section
        className={styles.features}
        aria-label="What Premium keeps doing"
      >
        {FEATURES.map((f) => (
          <div className={styles.feature} key={f.title}>
            <span className={styles.featureIcon}>
              <f.Icon className={styles.featureGlyph} />
            </span>
            <span className={styles.featureText}>
              <span className={styles.featureTitle}>{f.title}</span>
              <span className={styles.featureSub}>{f.sub}</span>
            </span>
          </div>
        ))}
      </section>

      {/* ── pricing: YEARLY featured, monthly small & secondary ─────────── */}
      <section className={styles.pricing} aria-label="Choose your plan">
        <button
          type="button"
          className={`${styles.yearly} ${plan === "yearly" ? styles.selected : ""}`}
          onClick={() => setPlan("yearly")}
          aria-pressed={plan === "yearly"}
        >
          <span className={styles.yearlyGlow} aria-hidden="true" />
          <span className={styles.yearlyHead}>
            <span className={styles.yearlyName}>
              The album that fills itself
            </span>
            <span className={styles.saveTag}>Save {savingsPct}%</span>
          </span>
          <span className={styles.yearlyPriceRow}>
            <span className={styles.yearlyPrice}>{yearlyPrice}</span>
            <span className={styles.yearlyUnit}>/ year</span>
            <span className={styles.check} aria-hidden="true">
              <svg viewBox="0 0 24 24" width="13" height="13">
                <path
                  d="M5 13l4 4L19 7"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </span>
          </span>
          <span className={styles.yearlyPerMo}>
            about {yearlyPerMonth}/mo, billed once a year · best value
          </span>
        </button>

        <button
          type="button"
          className={`${styles.monthly} ${plan === "monthly" ? styles.monthlySel : ""}`}
          onClick={() => setPlan("monthly")}
          aria-pressed={plan === "monthly"}
        >
          <span className={styles.monthlyName}>Just this page · Monthly</span>
          <span className={styles.monthlyPrice}>{monthlyPrice}/mo</span>
        </button>
      </section>

      {/* ── CTA ─────────────────────────────────────────────────────────── */}
      <button className={styles.cta} onClick={handleSubscribe}>
        <span className={styles.ctaSheen} aria-hidden="true" />
        <span className={styles.ctaTitle}>
          {plan === "yearly"
            ? hasTrial
              ? "Start my album — 7 days free"
              : "Start my album"
            : "Keep this page monthly"}
        </span>
        <span className={styles.ctaSub}>
          {plan === "yearly"
            ? hasTrial
              ? `7 days free, then ${yearlyPrice}/yr · cancel anytime`
              : `${yearlyPrice}/yr · cancel anytime`
            : `${monthlyPrice}/mo · cancel anytime`}
        </span>
      </button>

      <p className={styles.reassure}>
        Your photos are always yours — they never leave your vault and are never
        deleted. Premium just keeps your album filling itself.
      </p>

      <footer className={styles.legal}>
        <button onClick={() => native.postNativeEvent("terms")}>Terms</button>
        <span aria-hidden="true">·</span>
        <button onClick={() => native.postNativeEvent("privacy")}>
          Privacy
        </button>
        <span aria-hidden="true">·</span>
        <button onClick={() => native.postNativeEvent("already_purchased")}>
          Restore
        </button>
      </footer>
    </div>
  )
}

export default StickerAlbumPaywall
